import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import ScreenRecorder from '@/components/ScreenRecorder';
import { useAuthStore } from '@/stores/authStore';
import { Navigate } from 'react-router-dom';
import { RouteMap } from '@/routes/Routes';
import { hasBetaProductName } from '@/utils/subscriptionHelpers';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { useVideoUpload } from '@/pages/Record/Recorder/hooks';
import { UploadSection } from '@/pages/Record/Recorder/components';

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/** Metadata probes that hang are not rare for freshly-muxed WebM — bail out after this. */
const VIDEO_METADATA_TIMEOUT_MS = 2000;

/**
 * How far past the plan cap a recording may land before we treat it as a real
 * overrun rather than auto-stop jitter. The recorder's stop check runs on a
 * 200ms interval and container metadata rounds up, so a small overshoot is
 * expected and must not fail the upload.
 */
const CAP_OVERSHOOT_TOLERANCE_SECONDS = 2;

/**
 * Read a recorded file's duration, falling back to `fallbackSeconds` if the
 * browser never reports metadata.
 *
 * The timeout is load-bearing: without it, a WebM whose `loadedmetadata` and
 * `error` events both never fire leaves this promise permanently pending, and
 * the finished recording silently disappears — no upload, no error, nothing.
 */
const getVideoDuration = (file: File, fallbackSeconds: number): Promise<number> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };

    const resolveWith = (value: number) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : fallbackSeconds;
      resolveWith(duration);
    };
    video.onerror = () => {
      resolveWith(fallbackSeconds);
    };

    // Arm the safety timeout BEFORE kicking off the load, so `cleanup` always
    // has a defined id to clear no matter which path settles first.
    const timeoutId = window.setTimeout(() => {
      resolveWith(fallbackSeconds);
    }, VIDEO_METADATA_TIMEOUT_MS);

    video.src = url;
  });
};

const ScreenRecordingPage: React.FC = () => {
  const { userData } = useAuthStore();
  const planFeatures = useCurrentPlanFeatures();
  const planLimitSeconds = useMemo(() => planFeatures.video_seconds ?? 120, [planFeatures.video_seconds]);
  const planLimitRounded = useMemo(() => Math.round(planLimitSeconds), [planLimitSeconds]);
  const planLimitFormatted = useMemo(() => formatTime(planLimitRounded), [planLimitRounded]);

  const [featureAvailable, setFeatureAvailable] = useState<boolean>(true);
  const [includeMic, setIncludeMic] = useState(true);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);
  const [recordingDuration, setRecordingDuration] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const autoUploadTriggeredRef = useRef(false);

  const uploader = useVideoUpload({
    onUploadComplete: useCallback((result: { success: boolean }) => {
      if (result.success) setUploadComplete(true);
    }, []),
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaDevices = navigator.mediaDevices as MediaDevices & {
      getDisplayMedia?: (constraints?: DisplayMediaStreamOptions) => Promise<MediaStream>;
    };
    const mediaRecorderAvailable =
      typeof (window as typeof window & { MediaRecorder?: typeof MediaRecorder }).MediaRecorder !== 'undefined';
    const ok = !!(mediaDevices && typeof mediaDevices.getDisplayMedia === 'function' && mediaRecorderAvailable);
    setFeatureAvailable(ok);
  }, []);

  // Auto-upload when recording completes (matches video recorder behavior)
  useEffect(() => {
    if (
      recordedFile &&
      recordingDuration !== null &&
      !autoUploadTriggeredRef.current &&
      !uploader.isUploading &&
      !uploadComplete
    ) {
      if (recordingDuration <= planLimitSeconds) {
        autoUploadTriggeredRef.current = true;
        void uploader.uploadVideo(recordedFile);
      }
    }
    if (!recordedFile) {
      autoUploadTriggeredRef.current = false;
      setUploadComplete(false);
    }
  }, [recordedFile, recordingDuration, planLimitSeconds, uploader.isUploading, uploader.uploadVideo, uploadComplete]);

  const handleRecordingComplete = useCallback(
    async (file: File, reportedDurationSeconds: number) => {
      setError(null);

      try {
        let duration: number | null =
          Number.isFinite(reportedDurationSeconds) && reportedDurationSeconds > 0 ? reportedDurationSeconds : null;

        try {
          const metadataDuration = await getVideoDuration(file, reportedDurationSeconds);
          if (Number.isFinite(metadataDuration) && metadataDuration > 0) {
            duration = metadataDuration;
          }
        } catch {
          // Metadata reading failed, will use fallback duration
        }

        if (duration === null) {
          throw new Error('Could not determine the recording length.');
        }

        // Absorb overshoot from the auto-stop, but only overshoot. The recorder
        // stops itself at the plan cap; the 200ms timer tick and the container's
        // own metadata both land a fraction past it, and an unclamped value there
        // trips the over-limit check below, blocks auto-upload AND disables the
        // manual Upload button — destroying the recording we just stopped for the
        // user. Clamping in the hook alone is not enough: metadata overrides it.
        //
        // Only clamp inside a tolerance window. A blanket Math.min would make the
        // check below dead code and silently accept a genuinely over-cap file:
        // this page is the one flow where the tab is guaranteed to be backgrounded,
        // and Chrome throttles hidden-tab timers to >=1s (harder past 5 minutes),
        // so a real overrun is possible and must still be surfaced.
        if (duration <= planLimitSeconds + CAP_OVERSHOOT_TOLERANCE_SECONDS) {
          duration = Math.min(duration, planLimitSeconds);
        }

        setRecordingDuration(duration);
        setRecordedFile(file);

        if (duration > planLimitSeconds) {
          setError(
            `Your recording is ${formatTime(Math.round(duration))}, which exceeds your plan limit of ${planLimitFormatted}. Please record a shorter session.`,
          );
        }
      } catch {
        setError('We could not process the recorded video. Please try again.');
        setRecordedFile(null);
        setRecordingDuration(null);
      }
    },
    [planLimitFormatted, planLimitSeconds],
  );

  const handleUpload = useCallback(async () => {
    if (!recordedFile || recordingDuration === null || recordingDuration > planLimitSeconds) return;
    await uploader.uploadVideo(recordedFile);
  }, [recordedFile, recordingDuration, planLimitSeconds, uploader]);

  const handleReset = useCallback(() => {
    setRecordedFile(null);
    setRecordingDuration(null);
    setError(null);
    setUploadComplete(false);
    autoUploadTriggeredRef.current = false;
    uploader.clearUploadError();
  }, [uploader]);

  if (!hasBetaProductName(userData) && !planFeatures.betaFeatures.screenRecording && !planFeatures.isLoading) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  const hasRecording = !!recordedFile;

  return (
    <Box
      px={{ xs: '16px', sm: '32px' }}
      py="24px"
      sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '960px', mx: 'auto', gap: 3 }}
    >
      <Box>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Screen Recording
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Capture your screen and send it through the standard NoteGo video processing pipeline without leaving the app.
        </Typography>
      </Box>

      {!featureAvailable && (
        <Alert severity="warning">
          Screen capture is not available in this browser or connection. Please ensure you are using a supported
          browser.{` `}
          Use HTTPS or localhost and try again.
        </Alert>
      )}

      {!hasRecording && (
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Recording limit: {planLimitFormatted}
              </Typography>
              <Chip label={`Plan: ${planFeatures.planName ?? 'Current'}`} color="primary" variant="outlined" />
              <FormControlLabel
                control={<Switch checked={includeMic} onChange={(event) => setIncludeMic(event.target.checked)} />}
                label="Include microphone audio"
              />
            </Stack>
            <Box sx={{ mt: 3 }}>
              <ScreenRecorder
                includeMic={includeMic}
                preferredFps={30}
                filenameBase="screen_recording"
                onComplete={handleRecordingComplete}
                maxDuration={planLimitSeconds}
              />
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              Do not close or navigate away from this page while recording. Open links in a new tab or window instead.
            </Alert>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
              Click &ldquo;Start&rdquo; to choose the screen, window, or tab you would like to share. Recording stops
              when you click &ldquo;Done.&rdquo; Closing the shared window also ends the capture.
            </Typography>
          </CardContent>
        </Card>
      )}

      {hasRecording && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              {uploadComplete ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 6, sm: 8 },
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Upload Complete!
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Redirecting to your note...
                  </Typography>
                </Box>
              ) : (
                <>
                  {error && !uploader.isUploading && (
                    <Alert severity="error" onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  <UploadSection
                    isUploading={uploader.isUploading}
                    progress={uploader.uploadProgress}
                    error={uploader.uploadError}
                    selectedDuration={recordingDuration ?? 0}
                    maxDuration={planLimitSeconds}
                    onUpload={handleUpload}
                    onReset={handleReset}
                    onClearError={uploader.clearUploadError}
                    formatTime={formatTime}
                    recoveryFile={recordedFile ? { file: recordedFile, name: recordedFile.name } : null}
                  />
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ScreenRecordingPage;
